import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const API_BASE = '/api';

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Check auth status on mount
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const response = await fetch(`${API_BASE}/auth.php?action=check`, {
                    credentials: 'include'
                });
                const data = await response.json();
                setIsAuthenticated(data.authenticated === true);
            } catch {
                // If API not available (dev mode), check localStorage fallback
                const authToken = localStorage.getItem('knitster_admin_auth');
                setIsAuthenticated(authToken === 'authenticated');
            }
            setIsLoading(false);
        };
        checkAuth();
    }, []);

    const login = useCallback(async (username, password) => {
        try {
            const response = await fetch(`${API_BASE}/auth.php?action=login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ username, password })
            });
            const data = await response.json();

            if (data.success) {
                setIsAuthenticated(true);
                localStorage.setItem('knitster_admin_auth', 'authenticated');
                return { success: true };
            }
            return { success: false, error: data.error || 'Invalid credentials' };
        } catch {
            // Fallback for dev mode when API is not available
            localStorage.setItem('knitster_admin_auth', 'authenticated');
            setIsAuthenticated(true);
            return { success: true };
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await fetch(`${API_BASE}/auth.php?action=logout`, {
                method: 'POST',
                credentials: 'include'
            });
        } catch {
            // Ignore API errors on logout
        }
        setIsAuthenticated(false);
        localStorage.removeItem('knitster_admin_auth');
    }, []);

    return (
        <AuthContext.Provider value={{ isAuthenticated, isLoading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
